import React, { useEffect, useMemo, useState } from 'react';
import {
  AdminPageHeader,
  AdminDevRowMeta,
  btnLink,
  btnPrimary,
  btnSecondary,
  filtersRowClass,
  inputClass,
  selectClass,
} from '../adminUi';
import { useGrading } from './GradingContext';
import { GradingDeleteButton, GradingScoreRange } from './GradingShared';
import { compareDiffFactorOrder, confirmAndRunDelete, formatGradingScore, questionScoreRange } from './utils';
import {
  deleteGradingQuestion,
  getGradingQuestionUsage,
  moveGradingQuestion,
  type GradingQuestionUsage,
} from '../../../../services/gradingService';
import type { GradingDiffFactor, GradingQuestion } from './types';

function QuestionMovePanel({
  question,
  currentFactorName,
  familyFactors,
  onMoved,
}: {
  question: GradingQuestion;
  currentFactorName: string;
  familyFactors: GradingDiffFactor[];
  onMoved: () => Promise<void>;
}) {
  const candidates = useMemo(
    () => familyFactors
      .filter((f) => f.id !== question.diff_factor_id)
      .sort(compareDiffFactorOrder),
    [familyFactors, question.diff_factor_id],
  );
  const [targetFactorId, setTargetFactorId] = useState('');
  const [usage, setUsage] = useState<GradingQuestionUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    setUsageLoading(true);
    getGradingQuestionUsage(question.id)
      .then((data) => {
        if (!cancelled) setUsage(data);
      })
      .catch(() => {
        if (!cancelled) setUsage(null);
      })
      .finally(() => {
        if (!cancelled) setUsageLoading(false);
      });
    return () => { cancelled = true; };
  }, [question.id]);

  const handleMove = async () => {
    const target = candidates.find((f) => String(f.id) === targetFactorId);
    if (!target) {
      setError('Выберите целевой диффактор');
      return;
    }
    const usageNote = usage && usage.assessments > 0
      ? `\n\nСвязано с ${usage.assessments} анкет(ами) (${usage.answer_links} ответов в person_assessment_answers). ID вопроса и ответов не меняются — данные анкет сохранятся.`
      : '\n\nID вопроса и ответов не меняются — связанные анкеты не пострадают.';
    if (!window.confirm(
      `Перенести вопрос из «${currentFactorName}» в «${target.name}»?${usageNote}`,
    )) {
      return;
    }
    setMoving(true);
    setError('');
    setSuccess('');
    try {
      const result = await moveGradingQuestion(question.id, target.id, target.name);
      setSuccess(
        result.linkedAssessmentsPreserved != null && result.linkedAssessmentsPreserved > 0
          ? `Перенесено в «${result.targetFactorName}». Сохранено ${result.linkedAssessmentsPreserved} анкет.`
          : `Перенесено в «${result.targetFactorName || target.name}».`,
      );
      setTargetFactorId('');
      await onMoved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось перенести вопрос');
    } finally {
      setMoving(false);
    }
  };

  if (!candidates.length) {
    return (
      <p className="dc-grading-move-note">
        В этой Family нет других диффакторов для переноса.
      </p>
    );
  }

  return (
    <div className="dc-grading-move-panel">
      <p className="dc-grading-move-title">Перенести в другой диффактор</p>
      <p className="dc-grading-move-note">
        Меняется только <code>questions.diff_factor_id</code>. Ответы и связи с анкетами (
        <code>person_assessment_answers.question_id</code>, <code>answer_id</code>) сохраняются.
        {!usageLoading && usage && usage.assessments > 0 && (
          <> Сейчас используется в {usage.assessments} анкет(ах).</>
        )}
      </p>
      <div className="dc-grading-move-controls">
        <select
          className={selectClass}
          value={targetFactorId}
          disabled={moving}
          onChange={(e) => {
            setTargetFactorId(e.target.value);
            setError('');
            setSuccess('');
          }}
        >
          <option value="">— выберите диффактор —</option>
          {candidates.map((f) => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
        <button
          type="button"
          className={btnSecondary}
          disabled={!targetFactorId || moving}
          onClick={() => void handleMove()}
        >
          {moving ? 'Перенос…' : 'Перенести'}
        </button>
      </div>
      {error && <p className="dc-grading-move-error">{error}</p>}
      {success && <p className="dc-grading-move-success">{success}</p>}
    </div>
  );
}

export default function GradingQuestionsTab() {
  const { families, roleClusters, factors, questions, answers, openDrawer, reload } = useGrading();
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const [search, setSearch] = useState('');
  const [familyFilter, setFamilyFilter] = useState('');
  const [roleClusterFilter, setRoleClusterFilter] = useState('');

  const enriched = useMemo(() => {
    return questions.map((q) => {
      const factor = factors.find((f) => f.id === q.diff_factor_id);
      const rc = roleClusters.find((s) => s.id === q.role_cluster_id);
      const family = factor ? families.find((f) => f.id === factor.family_id) : undefined;
      const qAnswers = answers.filter((a) => a.question_id === q.id);
      const range = questionScoreRange(answers, q.id);
      return { q, factor, family, rc, qAnswers, range };
    }).filter(({ q, factor, family, rc, qAnswers }) => {
      const qSearch = search.trim().toLowerCase();
      if (qSearch) {
        const hit =
          q.question_text.toLowerCase().includes(qSearch) ||
          qAnswers.some((a) => a.answer_text.toLowerCase().includes(qSearch));
        if (!hit) return false;
      }
      if (familyFilter && Number(family?.id) !== Number(familyFilter)) return false;
      if (roleClusterFilter && Number(rc?.id) !== Number(roleClusterFilter)) return false;
      return true;
    }).sort((a, b) => {
      const factorOrder = compareDiffFactorOrder(
        a.factor ?? { sort_order: 9999, id: 9999 },
        b.factor ?? { sort_order: 9999, id: 9999 },
      );
      if (factorOrder !== 0) return factorOrder;
      const rcNameA = a.rc?.name ?? '';
      const rcNameB = b.rc?.name ?? '';
      if (rcNameA !== rcNameB) return rcNameA.localeCompare(rcNameB, 'ru');
      return a.q.id - b.q.id;
    });
  }, [questions, factors, roleClusters, families, answers, search, familyFilter, roleClusterFilter]);

  const filteredRoleClusters = roleClusters.filter((s) => !familyFilter || Number(s.family_id) === Number(familyFilter));

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div>
      <AdminPageHeader
        title="Вопросы и ответы"
        subtitle="Scoring-вопросы: diff_factor × role cluster. Перенос между диффакторами сохраняет связи с анкетами."
        actions={
          <button type="button" className={btnPrimary} onClick={() => openDrawer({ type: 'question', mode: 'create' })}>
            Добавить вопрос
          </button>
        }
      />

      <div className={filtersRowClass}>
        <input className={inputClass} placeholder="Поиск по тексту вопроса или ответа" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className={selectClass} value={familyFilter} onChange={(e) => { setFamilyFilter(e.target.value); setRoleClusterFilter(''); }}>
          <option value="">Все Family</option>
          {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <select className={selectClass} value={roleClusterFilter} onChange={(e) => setRoleClusterFilter(e.target.value)}>
          <option value="">Все Role cluster</option>
          {filteredRoleClusters.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      <div className="dc-grading-accordion">
        {enriched.length === 0 && <p className="text-sm text-slate-500">Нет вопросов</p>}
        {enriched.map(({ q, factor, family, rc, qAnswers, range }) => {
          const open = expanded.has(q.id);
          return (
            <div key={q.id} className={`dc-grading-question-card ${open ? 'dc-grading-question-card-open' : ''}`}>
              <button type="button" className="dc-grading-question-head" onClick={() => toggle(q.id)}>
                <div className="flex-1 text-left">
                  <p className="font-medium text-slate-900">{q.question_text}</p>
                  <AdminDevRowMeta parts={[
                    `questions.id=${q.id}`,
                    `questions.diff_factor_id=${q.diff_factor_id}`,
                    `questions.role_cluster_id=${q.role_cluster_id}`,
                  ]} />
                  <p className="dc-grading-meta mt-1">
                    {[family?.name, rc?.name, factor?.name].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-slate-500">{qAnswers.length} ответов</span>
                  <GradingScoreRange min={range.min} max={range.max} />
                  <i className={`fas fa-chevron-${open ? 'up' : 'down'} text-slate-400`} />
                </div>
              </button>
              {open && (
                <div className="dc-grading-question-body">
                  <ol className="dc-grading-answers-list">
                    {qAnswers.map((a, idx) => (
                      <li key={a.id}>
                        <span className="dc-grading-answer-num">{idx + 1}.</span>
                        <span className="flex-1">{a.answer_text}</span>
                        <AdminDevRowMeta parts={[`answers.id=${a.id}`, `answers.answer_score=${a.answer_score}`]} />
                        <span className="dc-grading-answer-score">{formatGradingScore(a.answer_score)} б.</span>
                      </li>
                    ))}
                  </ol>
                  {factor && family && (
                    <QuestionMovePanel
                      question={q}
                      currentFactorName={factor.name}
                      familyFactors={factors.filter((f) => f.family_id === family.id)}
                      onMoved={reload}
                    />
                  )}
                  <div className="mt-3 flex gap-3">
                    <button type="button" className={btnLink} onClick={() => openDrawer({ type: 'question', mode: 'edit', id: q.id })}>
                      Редактировать
                    </button>
                    <GradingDeleteButton
                      onClick={() => confirmAndRunDelete(
                        q.question_text.slice(0, 60),
                        'Вопрос и все ответы будут удалены.',
                        () => deleteGradingQuestion(q.id),
                        reload,
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
